// Harness 前端客户端：REST + SSE（供演示页与工作台面板共用）。
import type {
  HarnessEvent, HarnessApprovalAnswer, HarnessApprovalDecision, HarnessQuestionAnswer,
} from '@xiaotianshu/shared/harness';
import { logger } from '../utils/logger';

// ───────────────────────── 会话事件流水（完整落库）─────────────────────────
//
// 这是"会话历史无损"的写入端：把客户端收到的**每一个**引擎事件按序落库，
// 成为唯一权威来源。引擎的内存会话退化为热缓存，丢了就从流水重放重建。
//
// 与旧 `activity_snapshot` 的关键差别：
//   - 只追加（不整体覆盖）→ 多设备并发不会互相抹掉历史；
//   - 不截断（旧实现 4000 字/字段）→ 工具结果、整章正文原样保留；
//   - 含 reasoning / 审批往来 → 这些以前从未落库，重启即永久丢失。

/** 上报给服务端的事件形状（`event_id` 是幂等键，见下） */
export interface LedgerEventInput {
  /**
   * 客户端生成的**稳定**标识：同一次事件在重试时必须给出相同的 id。
   *
   * 由事件写入器按 `<sid>:<turnStartMs>:<index>` 生成 —— 一轮之内确定性，
   * 所以断线重连、整批重发都会被服务端识别为重复而不产生重复历史。
   * 同时也刻意**不依赖 localStorage**：跨设备/换浏览器都不会乱序或重号。
   */
  event_id: string;
  turn?: number;
  event_type: string;
  payload: unknown;
  occurred_at?: string;
}

/**
 * 追加一批事件流水。
 *
 * 失败**不上抛**给调用方流程（落库失败不应中断正在进行的对话），但会记录日志 ——
 * 静默失败正是本项目要消灭的那类故障，所以这里至少留下可查痕迹。
 */
export async function appendSessionEvents(
  sessionUuid: string,
  events: LedgerEventInput[],
  meta?: { modelName?: string; provider?: string },
): Promise<{ inserted: number; skipped: number } | null> {
  if (events.length === 0) return null;
  try {
    const res = await fetch(`/api/ai/chat-sessions/${encodeURIComponent(sessionUuid)}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        events,
        ...(meta?.modelName ? { model_name: meta.modelName } : {}),
        ...(meta?.provider ? { provider: meta.provider } : {}),
      }),
    });
    if (!res.ok) {
      logger.warn('appendSessionEvents failed', { sessionUuid, status: res.status, count: events.length });
      return null;
    }
    return await res.json() as { inserted: number; skipped: number };
  } catch (e) {
    logger.warn('appendSessionEvents error', { sessionUuid, error: e instanceof Error ? e.message : String(e) });
    return null;
  }
}

/** 读回事件流水（按 seq 升序）。不带 limit 即全量 —— 无损是默认行为。 */
export async function fetchSessionEvents(
  sessionUuid: string,
  options?: { fromSeq?: number; limit?: number },
): Promise<Array<{ seq: number; event_id: string; turn: number; event_type: string; payload: unknown; occurred_at?: string }>> {
  const params = new URLSearchParams();
  if (options?.fromSeq) params.set('fromSeq', String(options.fromSeq));
  if (options?.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  const res = await fetch(
    `/api/ai/chat-sessions/${encodeURIComponent(sessionUuid)}/events${qs ? `?${qs}` : ''}`,
    { credentials: 'include' },
  );
  if (!res.ok) throw new Error(`读取会话流水失败：${res.status}`);
  const data = await res.json() as { events: Array<{ seq: number; event_id: string; turn: number; event_type: string; payload: unknown; occurred_at?: string }> };
  return data.events ?? [];
}

/** 把引擎会话 id 绑定到 UI 会话（落库）—— 换设备后据此接回原会话 */
export async function bindHarnessSession(sessionUuid: string, harnessSessionId: string | null): Promise<void> {
  try {
    await fetch('/api/ai/chat-sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_uuid: sessionUuid, harness_session_id: harnessSessionId }),
    });
  } catch (e) {
    // 绑定失败不致命：本设备仍能用内存映射；下次启动会重新尝试
    logger.warn('bindHarnessSession failed', { sessionUuid, error: e instanceof Error ? e.message : String(e) });
  }
}

/**
 * 事件写入器：按轮次缓冲并自动分批上报。
 *
 * 为什么要缓冲：`text-delta` / `reasoning-delta` 是**逐字**推来的，一条一条上报
 * 会把数据库打爆（一个回答几千次 INSERT）。所以：
 *   - 增量文本由调用方**合并后**作为一条 `assistant-message` / `reasoning` 记录；
 *   - 其余结构化事件（工具调用/结果、审批、提问、回合结束）实时入队。
 *
 * 幂等：`event_id` 用 `<sid>:<turnStartMs>:<index>`，同一轮内稳定。
 */
export class SessionLedgerWriter {
  private queue: LedgerEventInput[] = [];
  private index = 0;
  private flushing = false;
  private readonly turnStartMs: number;
  /** 最近一次上报失败的原因（drain 未排空时用于给出可查的线索） */
  private lastError: Error | undefined;
  /** 上报函数（可注入，便于测试；生产用 {@link appendSessionEvents}） */
  private readonly send: typeof appendSessionEvents;

  constructor(
    private readonly sessionUuid: string,
    private readonly meta?: { modelName?: string; provider?: string },
    turnStartMs = Date.now(),
    /** 仅测试用：替换上报实现，以便在不发网络请求的前提下断言事件序列与幂等 id */
    send: typeof appendSessionEvents = appendSessionEvents,
  ) {
    this.turnStartMs = turnStartMs;
    this.send = send;
  }

  /** 记录一条事件（同步入队，立即尝试异步上报） */
  record(eventType: string, payload: unknown, turn = 0, occurredAt?: string): void {
    const eventId = `${this.sessionUuid}:${this.turnStartMs}:${this.index++}`;
    this.queue.push({
      event_id: eventId,
      turn,
      event_type: eventType,
      payload,
      occurred_at: occurredAt ?? new Date().toISOString(),
    });
    void this.flush();
  }

  /** 把队列里的内容上报（串行，避免乱序落库） */
  async flush(): Promise<void> {
    if (this.flushing) return;
    if (this.queue.length === 0) return;
    this.flushing = true;
    try {
      // 一次取走当前队列（后续 record 会写进新队列）
      while (this.queue.length > 0) {
        const batch = this.queue.splice(0, this.queue.length);
        try {
          await this.send(this.sessionUuid, batch, this.meta);
        } catch (e) {
          // 兜底吞掉：落库失败**绝不能**中断正在进行的对话，也绝不能让
          // 回合收尾路径（finish → drain）因为持久化问题而 reject。
          // 注意：吞掉的是"这一次尝试"，事件本身在这里**先不丢** ——
          // 放回队首，让 drain 有机会重试（见 drain 的说明）。
          this.queue.unshift(...batch);
          this.lastError = e instanceof Error ? e : new Error(String(e));
          logger.warn('session ledger flush failed', {
            sessionUuid: this.sessionUuid,
            count: batch.length,
            error: this.lastError.message,
          });
          return;
        }
      }
    } finally {
      this.flushing = false;
    }
  }

  /**
   * 回合结束前确保全部落库。
   *
   * 语义：**尽力而为，但不静默丢**。
   *   - 正常情况：排空队列后返回；
   *   - 上报持续失败：重试有限次（避免把回合结束卡死在一个永远失败的目标上），
   *     仍失败则打印**明确错误**后返回 —— 让"流水没写全"这件事可见，
   *     而不是悄悄少一段历史。事件仍保留在队列里，下一个回合的 drain 会再试。
   */
  async drain(maxAttempts = 3): Promise<void> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await this.flush();
      if (this.queue.length === 0) return;
    }
    if (this.queue.length > 0) {
      logger.warn('session ledger drain incomplete — events kept in memory for a later retry', {
        sessionUuid: this.sessionUuid,
        pending: this.queue.length,
        lastError: this.lastError?.message,
      });
    }
  }
}

export interface HarnessSetup {
  bookId: string;
  chapterId?: string;
  provider: 'deepseek' | 'openrouter';
  model: string;
  /** 模型推理力度（模型速率）：low / high / max；缺省用模型默认 */
  reasoningEffort?: string;
  /** 用量归因：UI 会话 id（convId），服务端以此记录 ai_usage */
  sessionUuid?: string;
  /** 要恢复的引擎会话 id；服务端会用 JSONL 日志 resume（服务重启/换设备后仍不失忆） */
  sessionId?: string;
  /** 历史摘要：注入引擎会话的上下文（模型可见、不触发轮次）；仅新建时使用 */
  primeText?: string;
}

export interface BookRow {
  id: string;
  title: string;
  description: string;
  word_count: number;
}

export interface VolumeRow {
  id: string;
  title: string;
  description: string;
  order_index: number;
}

export interface ChapterRow {
  id: string;
  title: string;
  chapter_number: number;
  summary: string;
  word_count: number;
  updated_at: string;
}

export interface ModelRow {
  id: string;
  name: string;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function fetchBooks(): Promise<BookRow[]> {
  const res = await fetch('/api/books', { credentials: 'include' });
  return jsonOrThrow<BookRow[]>(res);
}

export async function fetchVolumes(bookId: string): Promise<VolumeRow[]> {
  const res = await fetch(`/api/books/${bookId}/volumes`, { credentials: 'include' });
  return jsonOrThrow<VolumeRow[]>(res);
}

export async function fetchChapters(volumeId: string): Promise<ChapterRow[]> {
  const res = await fetch(`/api/volumes/${volumeId}/chapters`, { credentials: 'include' });
  return jsonOrThrow<ChapterRow[]>(res);
}

export async function fetchModels(provider: string): Promise<ModelRow[]> {
  const res = await fetch(`/api/ai/models?provider=${encodeURIComponent(provider)}`, { credentials: 'include' });
  const data = await jsonOrThrow<{ models: ModelRow[] }>(res);
  return data.models ?? [];
}

export async function fetchChapterContent(chapterId: string): Promise<string> {
  const res = await fetch(`/api/chapters/${chapterId}/content`, { credentials: 'include' });
  const data = await jsonOrThrow<{ content: string }>(res);
  return data.content ?? '';
}

/**
 * 读取某个 UI 会话在**数据库里**绑定的引擎会话 id。
 *
 * 这是跨设备续接的关键：localStorage 里的映射只对"这台设备"有效，
 * 换设备后必须能从库里查到"这个对话原本挂在哪个引擎会话上"，
 * 才能接回原上下文（而不是新建一个只有摘要的空会话）。
 *
 * 返回 `undefined` 表示库里没有映射（旧会话/已清空）→ 调用方走重建路径。
 */
export async function fetchBoundHarnessSession(sessionUuid: string): Promise<string | undefined> {
  try {
    const params = new URLSearchParams({ fields: 'meta' });
    const res = await fetch(`/api/ai/chat-sessions?${params.toString()}`, { credentials: 'include' });
    if (!res.ok) return undefined;
    const rows = await res.json() as Array<{ session_uuid: string; harness_session_id?: string | null }>;
    const row = rows.find((r) => r.session_uuid === sessionUuid);
    return row?.harness_session_id ?? undefined;
  } catch {
    return undefined;
  }
}

export async function createHarnessSession(setup: HarnessSetup): Promise<{ sessionId: string }> {
  const res = await fetch('/api/harness/sessions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(setup),
  });
  return jsonOrThrow<{ sessionId: string }>(res);
}

export async function fetchSessionInfo(sessionId: string): Promise<{
  provider: string; model: string; status: string;
  /** 还在等用户决策的审批/提问数量：刷新后据此决定要不要附着回去 */
  pendingApprovals?: number; pendingQuestions?: number;
}> {
  const res = await fetch(`/api/harness/sessions/${sessionId}`, { credentials: 'include' });
  return jsonOrThrow(res);
}

/**
 * **预检**：这个会话的事件流现在能不能连上？
 *
 * 用途是"先确认可前往，再提醒用户"。提醒本身只是打开面板并切到某个对话，
 * 但如果那一刻连不上（服务端刚重启、会话已被回收、应用还在启动），
 * 用户点下去只会什么都没发生 —— 所以先在提醒弹出的**之前**用同一个端点试一次。
 *
 * 只开一个短暂连接、只等打开就立刻关掉：不消费事件、不影响 UI、服务端
 * 那边只是多一次订阅（补发的待办事件我们不处理，也就不会重复弹卡）。
 */
export function probeHarnessStream(sessionId: string, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    let es: EventSource | null = null;
    const finish = (ok: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      es?.close();
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    try {
      es = new EventSource(`/api/harness/sessions/${sessionId}/events?replay=0`, { withCredentials: true });
      es.onopen = () => finish(true);
      es.onerror = () => finish(false);
    } catch {
      finish(false);
    }
  });
}

/**
 * 会话**还在不在**（用于把"暂时连不上"和"已经没了"分开）。
 *
 * 这个区分很重要，因为两种失败的收场方式完全不同：
 * - `unreachable`：服务端刚重启完/网络抖动，会话其实还在 → 值得重试；
 * - `gone`（404）：内存态的 DSH 会话随进程重启消失了，**挂着的审批/提问也已作废**
 *   （它没有落库）→ 重试毫无意义，只能如实告诉用户"这个待办救不回来了"。
 */
export async function probeSessionAlive(sessionId: string): Promise<'alive' | 'gone' | 'unreachable'> {
  try {
    const res = await fetch(`/api/harness/sessions/${sessionId}`, { credentials: 'include' });
    if (res.status === 404) return 'gone';
    if (!res.ok) return 'unreachable';
    return 'alive';
  } catch {
    return 'unreachable';
  }
}

/** 更新会话的模型推理力度（模型速率）；立即生效于下一轮请求 */
export async function updateHarnessSession(sessionId: string, patch: { reasoningEffort?: string }): Promise<void> {
  const res = await fetch(`/api/harness/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch),
  });
  await jsonOrThrow(res);
}

export async function sendSessionMessage(sessionId: string, text: string): Promise<void> {
  const res = await fetch(`/api/harness/sessions/${sessionId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ text }),
  });
  await jsonOrThrow(res);
}

export async function answerApproval(
  sessionId: string,
  requestId: string,
  decision: HarnessApprovalDecision,
  /**
   * 用户随审批写的意见（整体 + 逐条）。服务端会把它作为**模型可见上下文**注入会话，
   * 让 AI 直接读到用户的下一步要求或拒绝理由，而不是只知道"被拒了"。
   *
   * `systemDenyReason` 是**系统自动拒绝**（解析失败 / 没有审批界面）时带的原因：
   * 那条路径上用户根本没看到卡片，服务端据此向模型澄清"这不是用户拒绝"。
   */
  feedback?: {
    comment?: string;
    itemComments?: Array<{ index: number; comment: string }>;
    systemDenyReason?: HarnessApprovalAnswer['systemDenyReason'];
  },
): Promise<void> {
  const res = await fetch(`/api/harness/sessions/${sessionId}/approvals/${encodeURIComponent(requestId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      decision,
      ...(feedback?.comment ? { comment: feedback.comment } : {}),
      ...(feedback?.itemComments?.length ? { itemComments: feedback.itemComments } : {}),
      ...(feedback?.systemDenyReason ? { systemDenyReason: feedback.systemDenyReason } : {}),
    }),
  });
  await jsonOrThrow(res);
}

/** 回答 AI 的提问（`ask_user` 工具）。提问不设超时；会话结束时服务端会收尾。 */
export async function answerQuestion(
  sessionId: string,
  questionId: string,
  answer: HarnessQuestionAnswer,
): Promise<void> {
  const res = await fetch(`/api/harness/sessions/${sessionId}/questions/${encodeURIComponent(questionId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      selected: answer.selected ?? [],
      ...(answer.text ? { text: answer.text } : {}),
    }),
  });
  await jsonOrThrow(res);
}

export async function closeSession(sessionId: string): Promise<void> {
  await fetch(`/api/harness/sessions/${sessionId}`, { method: 'DELETE', credentials: 'include' });
}

/**
 * 订阅 SSE 事件流；返回关闭函数。
 * replay=false 时不重放历史（用于工作台面板：会话跨多次 run 复用，历史事件不应重复进入 UI）。
 */
export function openEventStream(
  sessionId: string,
  onEvent: (event: HarnessEvent) => void,
  onStateChange: (state: 'open' | 'closed') => void,
  options?: { replay?: boolean },
): () => void {
  const query = options?.replay === false ? '?replay=0' : '';
  const es = new EventSource(`/api/harness/sessions/${sessionId}/events${query}`, { withCredentials: true });
  es.addEventListener('harness', (e) => {
    const data = (e as MessageEvent).data as string;
    try {
      onEvent(JSON.parse(data) as HarnessEvent);
    } catch {
      /* 忽略无法解析的事件 */
    }
  });
  es.onopen = () => onStateChange('open');
  es.onerror = () => onStateChange('closed');
  return () => es.close();
}
